import { getBlockId, hasNewTabFlag, stripNewTabFlag, toCanonicalPath } from '@longy/common/cms'
import { useMemo } from 'react'

import { useRendererContext } from './RendererContext'

const ABSOLUTE = /^([a-z][a-z0-9+\-.]*:|\/\/)/i
const SAME_DOCUMENT = /^[#?]/

export type ResolvedLinkProps = {
    href: string
    target?: '_blank'
    rel?: 'noopener noreferrer'
}

/**
 * No renderer ever emits a raw href. Every stored or computed link goes through this, which applies
 * four transformations in a fixed order — that is the only way the canonical form and the new-tab
 * flags stay consistent across 28 renderers.
 */
export const resolveHref = (raw: string, { baseUrl }: { baseUrl: string }): string => {
    // 1. The per-link flag is never part of an href.
    const value = stripNewTabFlag(raw)
    if (!value) return value

    // 2. `block:<id>` becomes an in-page anchor, and it has to happen first: `block:` matches the
    //    absolute-url test below and would otherwise pass through as a bogus scheme.
    const blockId = getBlockId(value)
    if (blockId !== undefined) return `#${blockId}`

    // 3. The canonical form, ours to apply only to internal paths.
    const canonical = ABSOLUTE.test(value) ? value : toCanonicalPath(value)

    // 4. baseUrl, skipped for absolute urls and same-document references, which must stay relative
    //    to the current page.
    if (!baseUrl) return canonical
    if (ABSOLUTE.test(canonical)) return canonical
    if (SAME_DOCUMENT.test(canonical)) return canonical
    return `${baseUrl.replace(/\/$/, '')}${canonical}`
}

/** The new-tab decision is an OR: a context-wide flag never suppresses a per-link one. */
export const resolveLinkProps = (
    raw: string,
    { baseUrl, openLinksInNewTab }: { baseUrl: string; openLinksInNewTab: boolean },
): ResolvedLinkProps => {
    const href = resolveHref(raw, { baseUrl })
    const newTab = href !== '' && (hasNewTabFlag(raw) || openLinksInNewTab)
    return newTab ? { href, target: '_blank', rel: 'noopener noreferrer' } : { href }
}

export type LinkResolver = {
    resolveHref: (raw: string) => string
    resolveLinkProps: (raw: string) => ResolvedLinkProps
}

export const useLinkResolver = (): LinkResolver => {
    const { baseUrl, openLinksInNewTab } = useRendererContext()

    return useMemo(
        () => ({
            resolveHref: (raw: string) => resolveHref(raw, { baseUrl }),
            resolveLinkProps: (raw: string) =>
                resolveLinkProps(raw, { baseUrl, openLinksInNewTab }),
        }),
        [baseUrl, openLinksInNewTab],
    )
}

/**
 * A link that may be absent. Renderers use this to degrade to a non-link wrapper rather than
 * emitting an anchor with an empty href — which is what a deleted page target resolves to.
 */
export const useOptionalLink = (raw: string | null | undefined): ResolvedLinkProps | null => {
    const { resolveLinkProps: resolve } = useLinkResolver()
    if (!raw) return null
    const props = resolve(raw)
    return props.href ? props : null
}
